import { createRouter, createWebHistory } from 'vue-router';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { supabase } from '@/supabase';
import HomeView from '@/views/HomeView.vue';
import CardManagement from '@/views/CardManagement.vue';
import CardDepartments from '@/views/CardDepartments.vue';
import HowToPlay from '@/views/HowToPlay.vue';
import GameLogin from '@/components/GameLogin.vue';
import ResetAdminPassword from '@/views/ResetAdminPassword.vue';
import GameUsers from '@/views/GameUsers.vue';
import ManageDepartments from '@/views/ManageDepartments.vue';
import GameStatistics from '@/views/GameStatistics.vue';
import MyStats from '@/views/MyStats.vue';

const { isEnabled } = useFeatureFlags();

const routes = [
  {
    path: '/game-login',
    name: 'GameLogin',
    component: GameLogin
  },
  {
    path: '/reset-password',
    name: 'ResetAdminPassword',
    component: ResetAdminPassword
  },
  {
    path: '/game-users',
    name: 'GameUsers',
    component: GameUsers,
    meta: { requiresAdminAuth: true }
  },
  {
    path: '/manage-cards',
    name: 'CardManagement',
    component: CardManagement,
    meta: { requiresAdminAuth: true }
  },
  {
    path: '/manage-departments',
    name: 'ManageDepartments',
    component: ManageDepartments,
    meta: { requiresAdminAuth: true }
  },
  ...(isEnabled('USER_STATS') ? [{
    path: '/statistics',
    name: 'GameStatistics',
    component: GameStatistics,
    meta: { requiresAdminAuth: true }
  }, {
    path: '/my-stats',
    name: 'MyStats',
    component: MyStats,
    meta: { requiresGameAuth: true }
  }] : []),
  ...(isEnabled('HOW_TO_PLAY') ? [{
    path: '/how-to-play',
    name: 'HowToPlay',
    component: HowToPlay
  }] : []),
  {
    path: '/',
    name: 'CardDepartments',
    component: CardDepartments,
    meta: { requiresGameAuth: true }
  },
  {
    path: '/game/:departmentId',
    name: 'Game',
    component: HomeView,
    meta: { requiresGameAuth: true }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// ---- helpers ----

/**
 * Load the current user's department access from Supabase.
 * Returns array of { department_id, access_mode } or null on error.
 */
async function loadUserDeptAccess(userId) {
  try {
    const { data, error } = await supabase
      .from('user_department_access')
      .select('department_id, access_mode')
      .eq('user_id', userId);
    if (error) return null;
    return data || [];
  } catch {
    return null;
  }
}

/**
 * Return cached access from localStorage (written on login).
 * Falls back to a fresh fetch if the cache is missing.
 */
async function getUserDeptAccess(userId) {
  const cached = localStorage.getItem('userDeptAccess');
  if (cached) {
    try { return JSON.parse(cached); } catch { /* fall through */ }
  }
  const rows = await loadUserDeptAccess(userId);
  if (rows) localStorage.setItem('userDeptAccess', JSON.stringify(rows));
  return rows;
}

// ---- route guard ----

router.beforeEach(async (to, from, next) => {

  // Auth feature disabled → redirect to game-login for authenticated routes
  if (to.meta.requiresGameAuth && !isEnabled('GAME_AUTH')) {
    next('/game-login');
    return;
  }

  if (to.meta.requiresAdminAuth && !isEnabled('ADMIN_AUTH')) {
    next('/');
    return;
  }

  // Admin routes: let the component handle it
  if (to.meta.requiresAdminAuth) {
    next();
    return;
  }

  if (!to.meta.requiresGameAuth) {
    next();
    return;
  }

  // ---- Protected game routes ----

  // 1. Admin bypass (existing localStorage-based admin session)
  const loggedIn = localStorage.getItem('loggedIn');
  const adminUser = localStorage.getItem('adminUser');
  if (loggedIn === 'true' && adminUser) {
    next();
    return;
  }

  // 2. Check for a live Supabase Auth session (email-login users)
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      const userId = session.user.id;
      const accessRows = await getUserDeptAccess(userId);

      if (!accessRows || accessRows.length === 0) {
        // Signed in but no departments assigned
        await supabase.auth.signOut();
        localStorage.removeItem('gameSession');
        localStorage.removeItem('userDeptAccess');
        next('/game-login');
        return;
      }

      // Enforce department restriction on /game/:departmentId
      if (to.name === 'Game') {
        const targetId = Number(to.params.departmentId);
        const match = accessRows.find(r => r.department_id === targetId);

        if (!match) {
          // Not assigned to that department – send to departments picker
          next({ name: 'CardDepartments' });
          return;
        }

        // For demo_only access update game store session accordingly
        const gameSessionRaw = localStorage.getItem('gameSession');
        if (gameSessionRaw) {
          try {
            const gs = JSON.parse(gameSessionRaw);
            if (match.access_mode === 'demo_only' && !gs.isDemo) {
              gs.isDemo = true;
              gs.demoDepartmentId = targetId;
              localStorage.setItem('gameSession', JSON.stringify(gs));
            }
          } catch { /* ignore */ }
        }
      }

      // For CardDepartments route, no per-department check needed (component filters via RLS)
      next();
      return;
    }
  } catch (err) {
    console.error('Supabase session check error:', err);
  }

  // 3. Fall back to legacy game-code session (anonymous/facilitator)
  try {
    const gameSessionStr = localStorage.getItem('gameSession');
    if (!gameSessionStr) {
      next('/game-login');
      return;
    }

    const gameSession = JSON.parse(gameSessionStr);

    if (gameSession.expiresAt && Date.now() > gameSession.expiresAt) {
      localStorage.removeItem('gameSession');
      localStorage.removeItem('gameCode');
      next('/game-login');
      return;
    }

    // Code-based session must have a gameCode
    if (gameSession.authType === 'code' && !gameSession.gameCode) {
      next('/game-login');
      return;
    }

    // Demo session: lock to configured department
    if (gameSession.isDemo && gameSession.demoDepartmentId != null) {
      const demoId = String(gameSession.demoDepartmentId);
      if (to.name === 'CardDepartments') {
        next({ name: 'Game', params: { departmentId: demoId }, replace: true });
        return;
      }
      if (to.name === 'Game' && String(to.params.departmentId) !== demoId) {
        next({ name: 'Game', params: { departmentId: demoId }, replace: true });
        return;
      }
    }

    next();
    return;
  } catch (error) {
    console.error('Game session check error:', error);
    next('/game-login');
    return;
  }
});

export default router;
